import { TextFieldModule } from '@angular/cdk/text-field';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader, FileUploadModule } from 'ng2-file-upload';
import { catchError, defer, EMPTY, forkJoin, from, mergeMap, retry, tap, timer, toArray } from 'rxjs';

import { KeyValueModel } from '@models/common/keyValueModel';
import { MyDriveFileModel } from '@models/files/mydrive-file.model';
import { ProjectFileModel } from '@models/files/project-file.model';
import {
  FileChunkParams,
  FileItemModel,
  MimeTypesDict,
  MimeTypesEnum,
  MimeTypesNonStandardDict,
  PreviewableImageMimeTypes,
  ThumbnailsDict,
} from '@models/module/fields/main';
import { AddChunkedVersionParams, ProjectFilesAddNewModel, SaveFileInfoParams } from '@module-instance/my-drive/common';
import {
  FileFactoryService,
  FileServiceType,
  IFileService,
} from '@module-instance/my-drive/common/services/file-factory.service';
import { FILE_SIZES } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs/cipo-dialog-header/cipo-dialog-header.component';
import { DirtyDialogBase } from '@shared/dialogs/dirty-dialog.base';
import { CipoDialogModule } from '@shared/modules';
import { NotificationService, UserService } from '@shared/services';
import { DictionaryStore } from '@signal-store/dictionary.store';
import { UserStore } from '@signal-store/user.store';

@Component({
  selector: 'app-add-new',
  imports: [
    FileUploadModule,
    CipoDialogModule,
    MatProgressBarModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    TextFieldModule,
    CipoDialogHeaderComponent,
  ],
  providers: [FileFactoryService, NotificationService],
  templateUrl: './add-new.component.html',
  styleUrl: './add-new.component.scss',
})
export class AddNewComponent extends DirtyDialogBase {
  dialogData = inject<ProjectFilesAddNewModel>(MAT_DIALOG_DATA);
  translate = inject(TranslateService);
  userService = inject(UserService);
  fileFactoryService = inject(FileFactoryService);
  notificate = inject(NotificationService);
  userStore = inject(UserStore);
  private readonly dictionaryStore = inject(DictionaryStore);

  private readonly destroyRef = inject(DestroyRef);
  private readonly pendingUploads = new Set<FileItem>();
  private readonly previewUrls = new Map<FileItem, string>();
  private readonly maxPreviewBytes = 10 * 1024 * 1024;

  private supportedMimeTypes: KeyValueModel<number, string>[] = [];
  private readonly mimeTypesReady = signal(!this.dialogData.validateMimeTypes);

  name = new FormControl(this.dialogData.name || '', {
    validators: [Validators.required, Validators.maxLength(255)],
  });
  private readonly currentName = toSignal(this.name.valueChanges, { initialValue: this.name.value });
  private readonly selectedFileCount = signal(0);
  protected readonly isDirty = computed(() =>
    this.dialogData.modalType === 'newFile'
      ? this.selectedFileCount() > 0 || this.uploading()
      : this.currentName() !== (this.dialogData.name || ''),
  );

  valid = signal(false);
  uploading = signal(false);
  multiple = signal(true);
  createNewVersion = signal(true);
  filesToUpload: FileItemModel[] = [];
  dialogTitle = computed(() => {
    switch (this.dialogData.modalType) {
      case 'newFile':
        return 'fileExplorer.upload';
      case 'newFolder':
        return 'fileExplorer.addNewFolder';
      case 'rename':
        return 'fileExplorer.rename';
    }
  });

  uploader: FileUploader;
  hasBaseDropZoneOver: boolean;

  private fileService: IFileService<ProjectFileModel | MyDriveFileModel>;

  constructor() {
    super();
    this.fileService = this.fileFactoryService.getServiceType(
      this.dialogData.entityInstanceId ? FileServiceType.ProjectFiles : FileServiceType.MyDrive,
    );

    if (this.dialogData.fileId) {
      this.multiple.set(false);
    }

    if (this.dialogData.modalType !== 'newFile') {
      this.name.valueChanges.subscribe(() => {
        this.valid.set(this.name.valid);
      });
    }

    this.configureUploader();
    this.destroyRef.onDestroy(() => {
      this.uploading.set(false);
      this.uploader.cancelAll();
      this.releaseAllPreviews();
    });

    if (this.dialogData.validateMimeTypes) {
      this.loadSupportedMimeTypes();
    }
  }

  private configureUploader() {
    const headers = this.userService.getUserHeaders();

    this.uploader = new FileUploader({
      url: '',
      headers: Object.keys(headers).map(key => ({ name: key, value: headers[key] })),
    });

    this.uploader.onBeforeUploadItem = item => {
      item.withCredentials = false;
    };
    this.uploader.onAfterAddingFile = file => {
      const fileType = file.file.type;

      file.file['mimeType'] = fileType;
      if (this.rejectIfUnsupported(file)) {
        return;
      }
      this.setUploadUrl(file);
      file.file['preview'] = ThumbnailsDict[fileType as keyof typeof ThumbnailsDict] || ThumbnailsDict.default;
      // Avoid reading files into Base64 or decoding large images just for a thumbnail.
      if (PreviewableImageMimeTypes.includes(fileType as MimeTypesEnum) && file.file.size <= this.maxPreviewBytes) {
        const previewUrl = URL.createObjectURL(file._file);
        this.previewUrls.set(file, previewUrl);
        file.file['preview'] = previewUrl;
      }
      this.updateUploadValidity();
    };
    this.uploader.onCompleteItem = () => {
      this.updateUploadValidity();
    };
  }

  private loadSupportedMimeTypes() {
    this.dictionaryStore.getMimeTypesDict().subscribe({
      next: mimeTypes => {
        this.supportedMimeTypes = mimeTypes ?? [];
        this.mimeTypesReady.set(true);
        [...this.uploader.queue].forEach(file => this.rejectIfUnsupported(file));
        this.updateUploadValidity();
      },
      error: () => {
        this.supportedMimeTypes = [];
        this.mimeTypesReady.set(false);
        this.updateUploadValidity();
        this.notificate.error('fileExplorer.mimeTypesLoadFailed');
      },
    });
  }

  private rejectIfUnsupported(file: FileItem): boolean {
    if (!this.dialogData.validateMimeTypes || !this.mimeTypesReady()) {
      return false;
    }

    let mimeType = file.file['mimeType'] as string;
    const fileName = file.file.name;
    const extension = file.file.name?.split('.').pop() ?? '';

    // browser doesn't recognize the mime type, try to get it from the file extension from internal dictionary
    if (!mimeType) {
      mimeType = MimeTypesDict[extension];
    }

    // fix non standard mime types
    if (mimeType && MimeTypesNonStandardDict[mimeType]) {
      console.warn(
        'Non-standard mime type detected for file',
        fileName,
        ', replacing',
        mimeType,
        'with',
        MimeTypesNonStandardDict[mimeType],
      );
      mimeType = MimeTypesNonStandardDict[mimeType];
    }

    // if mime type is not recognized, try to find it by extension from backend dictionary
    if (!mimeType && extension) {
      const mimeTypeByExtension = (this.supportedMimeTypes ?? []).find(
        mt => mt.name?.toLocaleLowerCase() === `.${extension.toLocaleLowerCase()}`,
      )?.description;
      if (mimeTypeByExtension) {
        // If the mime type is not set but we have a mime type by extension, use it.
        console.warn('Mime type not set for file', fileName, ', using mime type by extension:', mimeTypeByExtension);
        mimeType = mimeTypeByExtension;
      }
    }

    // verify if the mime type exists in the supported mime types list from backend dictionary
    const existMimeType = mimeType && (this.supportedMimeTypes ?? []).some(mt => mt.description === mimeType);
    if (mimeType && existMimeType) {
      return false;
    }

    console.warn('Mime type not recognized or not supported for file', file.file.name, ', mime type:', mimeType);
    this.notificate.error('fileExplorer.unsupportedMimeType', {
      name: fileName,
      type: mimeType || extension,
    });
    this.releasePreview(file);
    this.uploader.removeFromQueue(file);
    this.updateUploadValidity();
    return true;
  }

  private updateUploadValidity() {
    this.selectedFileCount.set(this.uploader.queue.length);
    this.valid.set(!!this.uploader.queue.length && this.mimeTypesReady());
  }

  removeFile(file: FileItem) {
    this.releasePreview(file);
    file.remove();
    this.updateUploadValidity();
  }

  removeAllFiles() {
    this.releaseAllPreviews();
    this.uploader.clearQueue();
    this.filesToUpload = [];
    this.updateUploadValidity();
  }

  override discardAndClose(): void {
    this.uploading.set(false);
    this.pendingUploads.clear();
    this.uploader.cancelAll();
    super.discardAndClose();
  }

  private releasePreview(file: FileItem): void {
    const url = this.previewUrls.get(file);
    if (url) {
      URL.revokeObjectURL(url);
      this.previewUrls.delete(file);
    }
  }

  private releaseAllPreviews(): void {
    for (const file of this.previewUrls.keys()) {
      this.releasePreview(file);
    }
  }

  totalFileSize(): number {
    return this.uploader.queue.reduce((total, item) => total + item.file.size, 0);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  fileExtension(name: string): string {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.substring(dot + 1).toUpperCase() : '—';
  }

  fileOver(e: boolean): void {
    this.hasBaseDropZoneOver = e;
  }

  toggleCreateVersion(checked: boolean) {
    this.createNewVersion.set(checked);
    this.uploader.queue.forEach(file => this.setUploadUrl(file));
  }

  private setUploadUrl(file: FileItem) {
    const mimeType = file.file['mimeType'];
    file.url = this.dialogData.fileId
      ? this.fileService.getFileNewVersionUrl({
          fileId: this.dialogData.fileId,
          mimeType,
          entityInstanceId: this.dialogData.entityInstanceId,
        })
      : this.fileService.getFileUploadUrl({
          parentId: this.dialogData.parentId,
          mimeType,
          entityInstanceId: this.dialogData.entityInstanceId,
          isCreateVersion: this.dialogData.allowCreateVersion ? this.createNewVersion() : undefined,
        });
  }

  noteFor(item: FileItem): string {
    return (item.file['note'] as string) ?? '';
  }

  setNote(item: FileItem, note: string) {
    item.file['note'] = note;
  }

  private toUploadedFile(response: unknown): FileItemModel {
    if (typeof response === 'string') {
      return this.toUploadedFile(JSON.parse(response));
    }
    // AddVersion / AddChunkedVersion return the new version id, not a file DTO.
    if (typeof response === 'number') {
      return { id: this.dialogData.fileId, versionId: response } as FileItemModel;
    }
    return response as FileItemModel;
  }

  private applyNote(item: FileItem, file: FileItemModel): FileItemModel {
    const note = this.noteFor(item).trim();
    if (note) {
      file.note = note;
    }
    return file;
  }

  private closeWithUploadedFiles() {
    const files = this.filesToUpload;
    const withNotes = this.dialogData.persistNotes ? files.filter(f => f.note && f.versionId) : [];

    if (!withNotes.length) {
      this.dialogRef.close({ files });
      return;
    }

    forkJoin(
      withNotes.map(f =>
        this.fileService.saveFile({ id: f.id, versionId: f.versionId, note: f.note }, this.dialogData.entityInstanceId),
      ),
    ).subscribe({
      next: () => this.dialogRef.close({ files }),
      error: () => this.dialogRef.close({ files }),
    });
  }

  save() {
    if (this.dialogData.modalType === 'newFile') {
      if (this.uploading() || !this.valid() || !this.mimeTypesReady()) {
        return;
      }
      this.uploader.onSuccessItem = (item: FileItem, response: string) => {
        this.filesToUpload.push(this.applyNote(item, this.toUploadedFile(response)));
      };
      this.uploader.onErrorItem = (item: FileItem, response: string) => {
        const message = JSON.parse(response);
        this.notificate.error(message?.messages[0]?.message ?? 'errors.unidentifiedError');
      };
      this.uploader.onCompleteItem = item => {
        this.updateUploadValidity();
        this.completeUpload(item);
      };
      this.uploader.onCompleteAll = () => this.finishUploads();
      this.uploader.queue.forEach(file => {
        if (file.isError) {
          file.isError = false;
          file.isUploaded = false;
        }
      });
      const files = this.uploader.queue.filter(file => !file.isUploaded);
      // Register the entire batch before starting requests: completion may be synchronous.
      this.pendingUploads.clear();
      files.forEach(file => this.pendingUploads.add(file));
      this.uploading.set(true);
      files.forEach(file => {
        if (file.file.size > FILE_SIZES.maxFileSize) {
          this.uploadFileInChunks(file);
        } else {
          file.upload();
        }
      });
    } else {
      this.dialogRef.close({ name: this.name.value });
    }
  }

  private completeUpload(item: FileItem): void {
    this.pendingUploads.delete(item);
    this.finishUploads();
  }

  private finishUploads(): void {
    if (!this.uploading() || this.pendingUploads.size) {
      return;
    }
    if (this.uploader.queue.some(file => file.isError || !file.isUploaded)) {
      this.uploading.set(false);
      return;
    }
    // Keep controls disabled while notes are saved and ignore repeated completion callbacks.
    this.uploader.onCompleteAll = () => {};
    this.closeWithUploadedFiles();
  }

  private uploadFileInChunks(fileItem: FileItem) {
    const file = fileItem._file;
    const chunks = Math.ceil(file.size / FILE_SIZES.chunkSize);
    let fileId: string;

    this.fileService
      .generateFileId()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(id => (fileId = id)),
        catchError(() => {
          fileItem.isError = true;
          this.completeUpload(fileItem);
          return EMPTY;
        }),
      )
      .subscribe(() => {
        fileItem.progress = 100 / chunks / 2;
        let completedChunks = 0;
        const chunkIndices = Array.from({ length: chunks }, (_, index) => index);

        from(chunkIndices)
          .pipe(
            mergeMap(chunkIndex => {
              const start = chunkIndex * FILE_SIZES.chunkSize;
              const end = Math.min(start + FILE_SIZES.chunkSize, file.size);
              const uploadChunkParams: FileChunkParams = { start, end, chunks, file };

              return defer(() => {
                const formData = this.fileService.getChunkData(uploadChunkParams);
                return this.fileService.uploadChunk(fileId, formData);
              }).pipe(
                retry({
                  count: FILE_SIZES.chunkUploadMaxRetries,
                  delay: (_error, retryCount) =>
                    timer(FILE_SIZES.chunkUploadRetryDelayMs * Math.pow(2, retryCount - 1)),
                }),
                tap(() => {
                  completedChunks++;
                  if (completedChunks < chunks) {
                    fileItem.progress = (100 / chunks) * completedChunks;
                  }
                }),
              );
            }, FILE_SIZES.chunkUploadConcurrency),
            toArray(),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              fileItem.progress = 100;
              const subscriber = this.dialogData.fileId
                ? this.saveFileVersion(fileId, chunks, fileItem)
                : this.saveFileInfo(fileId, chunks, fileItem);
              subscriber
                .pipe(
                  takeUntilDestroyed(this.destroyRef),
                  catchError(() => {
                    fileItem.isError = true;
                    this.completeUpload(fileItem);
                    return EMPTY;
                  }),
                )
                .subscribe(responseFile => {
                  fileItem.isUploaded = true;
                  fileItem.isSuccess = true;
                  this.filesToUpload.push(this.applyNote(fileItem, this.toUploadedFile(responseFile)));
                  this.completeUpload(fileItem);
                });
            },
            error: err => {
              this.notificate.error(err);
              fileItem.isError = true;
              this.completeUpload(fileItem);
            },
          });
      });
  }

  private saveFileInfo(fileId: string, chunks: number, fileItem: FileItem) {
    const file = fileItem._file;
    const body = { fileName: file.name, bytes: file.size };
    const params: SaveFileInfoParams = {
      fileId,
      mimeType: file.type,
      totalBlocks: chunks,
      parentId: this.dialogData.parentId,
      roleId: 0,
      entityInstanceId: this.dialogData.entityInstanceId,
      isCreateVersion: this.createNewVersion(),
    };

    return this.fileService.saveFileInfo(params, body);
  }

  private saveFileVersion(fileId: string, chunks: number, fileItem: FileItem) {
    const file = fileItem._file;
    const body = { fileName: file.name, bytes: file.size };
    const params: AddChunkedVersionParams = {
      fileId: this.dialogData.fileId,
      mimeType: file.type,
      totalBlocks: chunks,
      roleId: 0,
      entityInstanceId: this.dialogData.entityInstanceId,
      physicalId: fileId,
    };

    return this.fileService.addChunkedVersion(params, body);
  }
}
